import React, { useState, useEffect, useRef, useCallback } from "react";
import { listTestRuns as getTestRuns, stopTestRun, clearTestRuns } from "../api/testRuns.js";
import VerificationBadge from "../components/VerificationBadge.jsx";

const RUNNING_STATUSES = new Set(["running", "stopping"]);

function isRunning(run) {
  return RUNNING_STATUSES.has(run?.status);
}

function formatDuration(seconds) {
  if (seconds == null || seconds < 0) return "--";
  if (seconds < 60) return `${seconds}s`;
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${m}m ${s}s`;
}

export default function RunHistory({ navigate }) {
  const [runs, setRuns] = useState([]);
  const [filter, setFilter] = useState("all");
  const [loading, setLoading] = useState(true);
  const [now, setNow] = useState(Date.now());
  const [stoppingIds, setStoppingIds] = useState(() => new Set());
  const [clearing, setClearing] = useState(false);
  const pollTimerRef = useRef(null);

  const refresh = useCallback(async () => {
    try {
      const data = await getTestRuns();
      setRuns(data);
    } catch (err) {
      console.error("Failed to load runs:", err);
    }
  }, []);

  // Initial load
  useEffect(() => {
    refresh().finally(() => setLoading(false));
  }, [refresh]);

  // Poll every 2s while any run is still running. Also tick a clock so
  // running-row durations update in real time.
  useEffect(() => {
    const hasRunning = runs.some(isRunning);
    if (!hasRunning) {
      if (pollTimerRef.current) {
        clearInterval(pollTimerRef.current);
        pollTimerRef.current = null;
      }
      return undefined;
    }
    // Tick immediately, then every 2 seconds
    setNow(Date.now());
    pollTimerRef.current = setInterval(() => {
      setNow(Date.now());
      refresh();
    }, 2000);
    return () => {
      if (pollTimerRef.current) {
        clearInterval(pollTimerRef.current);
        pollTimerRef.current = null;
      }
    };
  }, [runs, refresh]);

  const handleClearHistory = async () => {
    const pastRunCount = runs.filter((r) => !isRunning(r)).length;
    if (pastRunCount === 0) return;
    const msg = `Clear ${pastRunCount} past run${pastRunCount === 1 ? "" : "s"} for this tenant? Currently-running tests will NOT be affected. This cannot be undone.`;
    if (!confirm(msg)) return;
    setClearing(true);
    try {
      const result = await clearTestRuns();
      // Optimistic: drop all non-running rows; keep running rows so the
      // Stop button stays visible until the backend completes them.
      setRuns((prev) => prev.filter(isRunning));
      // Refresh to pick up the canonical list from the server.
      await refresh();
      const preservedNote = result.preserved > 0 ? ` (${result.preserved} active run(s) preserved)` : "";
      console.log(`[RUNS] Cleared ${result.removed} run(s)${preservedNote}`);
    } catch (err) {
      alert(`Failed to clear runs: ${err.message}`);
    } finally {
      setClearing(false);
    }
  };

  const handleStop = async (e, runId) => {
    e.stopPropagation(); // don't trigger the row click → detail navigation
    if (stoppingIds.has(runId)) return;
    if (!confirm(`Stop run ${runId}? The call will be aborted.`)) return;
    setStoppingIds((prev) => new Set(prev).add(runId));
    try {
      await stopTestRun(runId);
      // Optimistically mark the row as stopping until the next poll picks up
      // the final status from the server.
      setRuns((prev) =>
        prev.map((r) => (r.id === runId ? { ...r, status: "stopping" } : r))
      );
      // Trigger an immediate refresh; the regular poll will also pick it up.
      refresh();
    } catch (err) {
      alert(`Failed to stop run: ${err.message}`);
    } finally {
      setStoppingIds((prev) => {
        const next = new Set(prev);
        next.delete(runId);
        return next;
      });
    }
  };

  const filtered = runs.filter((r) => {
    if (filter === "passed") return r.verification?.passed;
    if (filter === "failed") return r.verification && !r.verification.passed;
    if (filter === "running") return isRunning(r);
    return true;
  });

  const runningCount = runs.filter(isRunning).length;

  if (loading) return <div style={s.loading}>Loading...</div>;

  return (
    <div>
      <div style={s.header}>
        <div style={s.titleRow}>
          <h1 style={s.title}>Run History</h1>
          {runningCount > 0 && (
            <span style={s.runningPill}>
              <span style={s.runningDot} /> {runningCount} running
            </span>
          )}
        </div>
        <div style={s.headerActions}>
          <div style={s.filters}>
            {["all", "running", "passed", "failed"].map((f) => (
              <button
                key={f}
                style={{ ...s.filterBtn, ...(filter === f ? s.filterActive : {}) }}
                onClick={() => setFilter(f)}
              >
                {f.charAt(0).toUpperCase() + f.slice(1)}
              </button>
            ))}
          </div>
          <button
            style={{
              ...s.clearBtn,
              ...(clearing || runs.filter((r) => !isRunning(r)).length === 0 ? s.clearBtnDisabled : {}),
            }}
            onClick={handleClearHistory}
            disabled={clearing || runs.filter((r) => !isRunning(r)).length === 0}
            title="Clear all past runs for this tenant (active runs are preserved)"
          >
            {clearing ? "Clearing…" : "Clear History"}
          </button>
        </div>
      </div>

      {filtered.length === 0 ? (
        <div style={s.empty}>
          <p style={s.emptyText}>No test runs yet</p>
          <p style={s.emptySub}>Run a test case from the Dashboard to see results here.</p>
        </div>
      ) : (
        <div style={s.tableWrap}>
          <table style={s.table}>
            <thead>
              <tr>
                <th style={s.th}>Run ID</th>
                <th style={s.th}>Test Case</th>
                <th style={s.th}>Patient</th>
                <th style={s.th}>Date</th>
                <th style={s.th}>Duration</th>
                <th style={s.th}>Status</th>
                <th style={s.th}>Result</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((r) => {
                const running = isRunning(r);
                const liveDuration = running && r.startedAt
                  ? Math.max(0, Math.round((now - new Date(r.startedAt).getTime()) / 1000))
                  : r.duration;
                return (
                  <tr
                    key={r.id}
                    style={{ ...s.tr, ...(running ? s.trRunning : {}) }}
                    onClick={() => navigate("run-detail", { runId: r.id })}
                  >
                    <td style={s.td}><span style={s.id}>{r.id}</span></td>
                    <td style={s.td}>
                      <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                        <span style={s.name}>{r.testCaseName || r.testCaseId}</span>
                        {r.configMode && (
                          <span style={r.configMode === "live" ? s.liveBadge : s.draftBadge}>
                            {r.configMode}
                          </span>
                        )}
                      </div>
                    </td>
                    <td style={s.td}>
                      <span style={s.patient}>{r.patientName}</span>
                      <span style={s.patientSub}>{r.patientId}</span>
                    </td>
                    <td style={s.td}>{r.startedAt ? new Date(r.startedAt).toLocaleString() : "--"}</td>
                    <td style={s.td}>{formatDuration(liveDuration)}</td>
                    <td style={s.td}>
                      {running ? (
                        <div style={s.statusCell}>
                          <span style={s.statusRunning}>
                            <span style={s.runningDotSmall} />
                            {r.status === "stopping" ? "Stopping" : "Running"}
                          </span>
                          {r.status !== "stopping" && (
                            <button
                              style={{
                                ...s.stopBtn,
                                ...(stoppingIds.has(r.id) ? s.stopBtnDisabled : {}),
                              }}
                              onClick={(e) => handleStop(e, r.id)}
                              disabled={stoppingIds.has(r.id)}
                              title="Stop this run"
                            >
                              Stop
                            </button>
                          )}
                        </div>
                      ) : r.status === "completed" ? (
                        <span style={s.statusOk}>completed</span>
                      ) : r.status === "stopped" ? (
                        <span style={s.statusStopped}>stopped</span>
                      ) : (
                        <span style={s.statusErr}>{r.status}</span>
                      )}
                    </td>
                    <td style={s.td}><VerificationBadge verification={r.verification} /></td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

const s = {
  loading: { padding: 60, textAlign: "center", color: "#999", fontSize: 14 },
  header: {
    display: "flex", justifyContent: "space-between", alignItems: "center",
    padding: "20px 32px 0",
  },
  titleRow: { display: "flex", alignItems: "center", gap: 12 },
  title: { fontSize: 22, fontWeight: 600, letterSpacing: "-0.02em" },
  runningPill: {
    display: "inline-flex", alignItems: "center", gap: 6,
    padding: "3px 10px", borderRadius: 12,
    background: "#eff6ff", color: "#1d4ed8",
    fontSize: 11, fontWeight: 500,
  },
  runningDot: {
    width: 7, height: 7, borderRadius: "50%",
    background: "#1d4ed8", animation: "pulse 1.5s ease-in-out infinite",
  },
  runningDotSmall: {
    width: 6, height: 6, borderRadius: "50%",
    background: "#1d4ed8", animation: "pulse 1.5s ease-in-out infinite",
  },
  headerActions: { display: "flex", alignItems: "center", gap: 10 },
  filters: { display: "flex", gap: 2, background: "#f5f5f5", borderRadius: 8, padding: 2 },
  clearBtn: {
    padding: "6px 14px", background: "#fff", color: "#dc2626",
    border: "1px solid #fecaca", borderRadius: 8,
    fontSize: 13, fontWeight: 500, cursor: "pointer",
  },
  clearBtnDisabled: { opacity: 0.5, cursor: "not-allowed" },
  filterBtn: {
    padding: "5px 14px", border: "none", borderRadius: 6,
    background: "transparent", fontSize: 13, fontWeight: 500, color: "#666",
  },
  filterActive: { background: "#fff", color: "#0f0f0f", boxShadow: "0 1px 2px rgba(0,0,0,0.06)" },
  empty: {
    margin: "16px 32px", border: "1px solid #e5e5e5", borderRadius: 10,
    padding: "48px 20px", textAlign: "center",
  },
  emptyText: { fontSize: 15, fontWeight: 500, color: "#333" },
  emptySub: { fontSize: 13, color: "#999", marginTop: 4 },
  tableWrap: {
    margin: "16px 32px 32px", border: "1px solid #e5e5e5", borderRadius: 10, overflow: "auto",
  },
  table: { width: "100%", borderCollapse: "collapse" },
  th: {
    padding: "10px 16px", textAlign: "left", fontSize: 12, fontWeight: 500,
    color: "#666", borderBottom: "1px solid #e5e5e5", background: "#fafafa",
  },
  tr: { borderBottom: "1px solid #f0f0f0", cursor: "pointer" },
  trRunning: { background: "#f5f9ff" },
  td: { padding: "10px 16px", fontSize: 13 },
  id: { fontFamily: "monospace", fontSize: 12, color: "#999" },
  name: { fontWeight: 500, color: "#0f0f0f" },
  patient: { fontWeight: 500, color: "#0f0f0f", display: "block", fontSize: 13 },
  patientSub: { fontSize: 11, color: "#999", fontFamily: "monospace" },
  statusCell: { display: "flex", alignItems: "center", gap: 8 },
  statusRunning: {
    display: "inline-flex", alignItems: "center", gap: 6,
    padding: "2px 10px", borderRadius: 10,
    background: "#eff6ff", color: "#1d4ed8",
    fontSize: 12, fontWeight: 500,
  },
  statusOk: {
    padding: "2px 8px", borderRadius: 10, background: "#ecfdf5",
    color: "#059669", fontSize: 12, fontWeight: 500,
  },
  statusStopped: {
    padding: "2px 8px", borderRadius: 10, background: "#fef3c7",
    color: "#92400e", fontSize: 12, fontWeight: 500,
  },
  statusErr: {
    padding: "2px 8px", borderRadius: 10, background: "#fef2f2",
    color: "#dc2626", fontSize: 12, fontWeight: 500,
  },
  draftBadge: {
    display: "inline-block", padding: "1px 6px", borderRadius: 4,
    background: "#eff6ff", color: "#1d4ed8", fontSize: 10, fontWeight: 600,
    letterSpacing: "0.03em", textTransform: "uppercase",
  },
  liveBadge: {
    display: "inline-block", padding: "1px 6px", borderRadius: 4,
    background: "#fef2f2", color: "#dc2626", fontSize: 10, fontWeight: 600,
    letterSpacing: "0.03em", textTransform: "uppercase",
  },
  stopBtn: {
    padding: "3px 10px", background: "#fff", color: "#dc2626",
    border: "1px solid #fecaca", borderRadius: 6,
    fontSize: 11, fontWeight: 500, cursor: "pointer",
  },
  stopBtnDisabled: { opacity: 0.5, cursor: "wait" },
};
